import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import SEOHead from "@/components/SEOHead";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<"loading" | "valid" | "already" | "error" | "done">("loading");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }

    const validate = async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const res = await fetch(
          `${supabaseUrl}/functions/v1/handle-email-unsubscribe?token=${token}`,
          { headers: { apikey } }
        );
        const data = await res.json();
        if (res.ok && data.valid) {
          setStatus("valid");
        } else if (data.reason === "already_unsubscribed") {
          setStatus("already");
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      }
    };
    validate();
  }, [token]);

  const handleUnsubscribe = async () => {
    try {
      const { data } = await supabase.functions.invoke("handle-email-unsubscribe", {
        body: { token },
      });
      if (data?.success) {
        setStatus("done");
      } else if (data?.reason === "already_unsubscribed") {
        setStatus("already");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <main className="min-h-screen bg-[#0B0B0B] flex items-center justify-center px-6">
      <SEOHead title="Unsubscribe — Lumient" description="Manage your Lumient email preferences." noindex />
      <div className="max-w-md w-full text-center">
        {status === "loading" && (
          <p className="text-surface-inverse-muted text-sm">Validating…</p>
        )}
        {status === "valid" && (
          <>
            <h1 className="text-2xl font-serif text-white mb-4">Unsubscribe</h1>
            <p className="text-surface-inverse-muted text-sm mb-8">
              Click below to unsubscribe from Lumient emails.
            </p>
            <button
              onClick={handleUnsubscribe}
              className="rounded-full bg-primary text-white px-8 py-3 text-sm font-medium tracking-wide transition-all duration-300 hover:bg-[oklch(0.52_0.12_65)]"
            >
              Confirm Unsubscribe
            </button>
          </>
        )}
        {status === "done" && (
          <>
            <h1 className="text-2xl font-serif text-white mb-4">Unsubscribed</h1>
            <p className="text-surface-inverse-muted text-sm">
              You've been successfully unsubscribed. You won't receive any more emails from us.
            </p>
          </>
        )}
        {status === "already" && (
          <>
            <h1 className="text-2xl font-serif text-white mb-4">Already unsubscribed</h1>
            <p className="text-surface-inverse-muted text-sm">
              This email address has already been unsubscribed.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="text-2xl font-serif text-white mb-4">Something went wrong</h1>
            <p className="text-surface-inverse-muted text-sm">
              This unsubscribe link is invalid or has expired.
            </p>
          </>
        )}
      </div>
    </main>
  );
};

export default Unsubscribe;
